<template>
  <div class="model-generator-detail">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <el-spinner size="large"></el-spinner>
      <p>Loading job details...</p>
    </div>

    <!-- Detail Content -->
    <div v-else-if="jobDetail" class="detail-content">
      <!-- Metadata Section -->
      <div class="metadata-section">
        <div class="metadata-header">
          <h4>Job ID: {{ jobId }}</h4>
        </div>
        <JobParameters :parameters="rawParameters" :status="jobDetail.status" />
        <div class="metadata-content">
          <!-- Progress Bar (shown when processing) -->
          <div
            v-if="
              jobDetail.status === 'Processing' ||
                jobDetail.status === 'Pending'
            "
            class="progress-container"
          >
            <label>Progress:</label>
            <el-progress
              :percentage="jobDetail.progressPercentage || 0"
              :text-inside="true"
              :stroke-width="25"
              status="success"
            ></el-progress>
          </div>

          <!-- Error Message (shown when job failed) -->
          <div v-if="jobDetail.status === 'Failed'" class="error-message">
            <i class="el-icon-warning"></i>
            <span>Job execution failed. No results available.</span>
          </div>

          <!-- Parameters Section -->
          <div
            v-if="jobDetail.status === 'Completed' && jobDetail.details"
            class="parameters-section"
          >
            <div class="parameters-grid">
              <div class="parameter-item">
                <label>Total Raw Data Points:</label>
                <span>{{ jobDetail.details.totalRawDataPoints || 0 }}</span>
              </div>
              <div class="parameter-item">
                <label>Total Bucket Data Points:</label>
                <span>{{ jobDetail.details.totalBucketDataPoints || 0 }}</span>
              </div>
              <div class="parameter-item">
                <label>Pad Cycles Used:</label>
                <span>{{
                  jobDetail.details.totalNumberOfPadCycleUsed || 0
                }}</span>
              </div>
              <div class="parameter-item">
                <label>Total Pad Cycles:</label>
                <span>{{ jobDetail.details.totalNumberOfPadCycle || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Results Section -->
          <div
            v-if="
              jobDetail.status === 'Completed' &&
                jobDetail.details &&
                jobDetail.details.results
            "
            class="results-section"
          >
            <h5>Results</h5>
            <div class="results-grid">
              <div class="result-item">
                <label>A:</label>
                <span>{{ jobDetail.details.results.a }}</span>
              </div>
              <div class="result-item">
                <label>B:</label>
                <span>{{ jobDetail.details.results.b }}</span>
              </div>
              <div class="result-item">
                <label>C:</label>
                <span>{{ jobDetail.details.results.c }}</span>
              </div>
              <div class="result-item">
                <label>Final RMSE:</label>
                <span>{{ jobDetail.details.results.finalRMSE }}</span>
              </div>
              <div class="result-item">
                <label>Outliers Removed:</label>
                <span>{{
                  jobDetail.details.results.outliersRemoved || 0
                }}</span>
              </div>
            </div>

            <!-- Equation Section -->
            <div class="equation-section">
              <span class="equation-label">Equation:</span>
              <el-input
                v-model="editingEquation"
                placeholder="Enter equation"
                size="small"
                class="equation-input"
              />
              <el-button
                type="primary"
                size="small"
                @click="saveEquation"
                :loading="savingEquation"
              >
                SAVE
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart Section -->
      <div
        v-if="jobDetail.status === 'Completed' && hasChartData"
        class="chart-section"
      >
        <div class="chart-controls">
          <div class="chart-actions">
            <el-button @click="downloadChartData" type="primary" size="small">
              Download Chart Data
            </el-button>
            <el-button @click="resetZoom" size="small">
              Reset Zoom
            </el-button>
          </div>
        </div>
        <div id="chart-container" class="chart-container"></div>
      </div>

      <!-- Data Table Section -->
      <div
        v-if="jobDetail.status === 'Completed' && jobDetail.chartData"
        class="table-section"
      >
        <BackTestDataTable
          :data="jobDetail.chartData"
          :loading="false"
          :filters="activeFilters"
        />
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="error-state">
      <p>Failed to load job details</p>
      <el-button @click="loadJobDetail" type="primary">Retry</el-button>
    </div>
  </div>
</template>

<script>
import BackTestApiService from '@/services/BackTestApiService'
import BackTestDataTable from '@/components/backtest/BackTestDataTable.vue'
import JobParameters from '@/components/backtest/JobParameters.vue'
import * as echarts from 'echarts'
import swal from 'sweetalert'
import { mapState } from 'vuex'

export default {
  name: 'ModelGeneratorDetail',
  components: {
    BackTestDataTable,
    JobParameters
  },
  props: {
    jobId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      jobDetail: null,
      loading: false,
      rawParameters: {},
      pollingInterval: null,
      savingEquation: false,
      hasChartData: false,
      chartInstance: null,
      resizeHandler: null,
      editingEquation: '',
      // Table data
      selectedData: [],
      activeFilters: []
    }
  },
  mounted() {
    this.loadJobDetail()
  },
  watch: {
    hasChartData(newVal) {
      if (newVal) {
        // Wait for DOM to render the chart-container element
        this.$nextTick().then(() => {
          // Add another tick to ensure DOM is fully rendered
          this.$nextTick(() => {
            this.initChart()
          })
        })
      }
    }
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
  },
  computed: {
    ...mapState('product', ['products'])
  },
  methods: {
    async loadJobDetail() {
      this.loading = true
      try {
        const response = await BackTestApiService.getBackTestDetail(this.jobId)
        const data = response.data

        // Parse Parameters from JSON string
        let parameters = {}
        if (typeof data.Parameters === 'string') {
          parameters = JSON.parse(data.Parameters)
        } else {
          parameters = data.Parameters || {}
        }

        // Store raw parameters for display
        this.rawParameters = parameters

        // Extract Details if available
        const details = data.Details || {}
        const results = details.Results || {}

        // Build jobDetail object from API response
        this.jobDetail = {
          id: data.Id,
          status: data.Status,
          progressPercentage: data.Progress || 0,
          jobType: data.JobType,
          // Metadata from parameters
          productId: parameters.productId || '',
          rmse: parameters.rmse || 0,
          // URLs
          chartUrl: data.ChartUrl || '',
          rawUrl: data.RawUrl || '',
          details: {
            totalBucketDataPoints: details.TotalBucketDataPoints || 0,
            totalNumberOfPadCycle: details.TotalNumberOfPadCycle || 0,
            totalNumberOfPadCycleUsed: details.TotalNumberOfPadCycleUsed || 0,
            totalRawDataPoints: details.TotalRawDataPoints || 0,
            results: {
              a: results.A || 0,
              b: results.B || 0,
              c: results.C || 0,
              finalRMSE: results.FinalRMSE || 0,
              outliersRemoved: results.OutliersRemoved || 0,
              equation: results.Equation || ''
            }
          }
        }

        // Initialize editing equation
        this.editingEquation =
          (this.jobDetail.details &&
            this.jobDetail.details.results &&
            this.jobDetail.details.results.equation) ||
          ''

        // If completed and has chart URL, load and parse chart data
        if (this.jobDetail.status === 'Completed' && this.jobDetail.chartUrl) {
          try {
            await this.loadChartDataFromUrl(this.jobDetail.chartUrl)
            // Set hasChartData to trigger watch and initialize chart
            this.hasChartData = true
          } catch (error) {
            this.hasChartData = false
          }
        }

        // Start polling if status is Processing or Queued
        if (
          this.jobDetail.status === 'Processing' ||
          this.jobDetail.status === 'Queued'
        ) {
          this.startPolling()
        }
      } finally {
        this.loading = false
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingInterval = setInterval(async () => {
        const response = await BackTestApiService.getBackTestStatus(this.jobId)
        const data = response.data
        this.jobDetail.status = data.Status
        this.jobDetail.progressPercentage = data.Progress

        // Stop polling when complete or failed
        if (data.Status === 'Completed' || data.Status === 'Failed') {
          this.stopPolling()
          this.loadJobDetail() // Reload full details
        }
      }, 3000)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    getProductName(productId) {
      if (!productId || !this.products || this.products.length === 0) {
        return `-`
      }
      const product = this.products.find(p => p.id === productId)
      return product ? product.name : `-`
    },

    async loadChartDataFromUrl(chartUrl) {
      const result = await BackTestApiService.fetchChartData(chartUrl)
      // result is { headers: [], data: [] }
      this.jobDetail.chartData = result.data || result
    },

    renderChart() {
      const container = document.querySelector('#chart-container')

      if (!container) {
        return
      }

      if (!this.jobDetail || !this.jobDetail.chartData) {
        return
      }

      if (this.chartInstance) {
        this.chartInstance.dispose()
      }

      this.chartInstance = echarts.init(container)

      const chartData = this.jobDetail.chartData || []
      if (chartData.length === 0) {
        return
      }

      // Prepare scatter data and outliers
      const colorMap = new Map()
      const colorPalette = [
        '#5470c6',
        '#91419f',
        '#ee6666',
        '#73c0de',
        '#3ba272',
        '#fc8452',
        '#9a60b4',
        '#ea7ccc'
      ]
      let colorIndex = 0

      const scatterData = []
      const outliersData = []

      // Parse and group data points
      chartData.forEach(point => {
        const sensorValue = parseFloat(
          point.SensorValue || point.sensorValue || 0
        )
        const padWeight = parseFloat(point.PadWeight || point.padWeight || 0)
        const isOutlier =
          String(
            point.IsOutliers || point.isOutliers || 'false'
          ).toLowerCase() === 'true'
        const colorId = point.ColorId || point.colorId || 0

        if (isOutlier) {
          outliersData.push([sensorValue, padWeight])
        } else {
          if (!colorMap.has(colorId)) {
            colorMap.set(
              colorId,
              colorPalette[colorIndex % colorPalette.length]
            )
            colorIndex++
          }
          scatterData.push({
            value: [sensorValue, padWeight],
            itemStyle: { color: colorMap.get(colorId) }
          })
        }
      })

      // Generate equation curve data
      const equationCurveData = this.generateEquationCurveData(chartData)

      const option = {
        title: {
          text: 'Volume Estimation Model',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross'
          },
          formatter: function(params) {
            if (!params.value) return ''
            const [sensorValue, padWeight] = params.value
            return `Sensor Value: ${sensorValue.toFixed(
              2
            )}<br/>Pad Weight: ${padWeight.toFixed(2)}`
          }
        },
        legend: {
          data: ['Data Points', 'Outliers', 'Equation Curve'],
          top: 30
        },
        xAxis: {
          type: 'value',
          name: 'Sensor Value',
          nameLocation: 'middle',
          nameGap: 30
        },
        yAxis: {
          type: 'value',
          name: 'Pad Weight',
          nameLocation: 'middle',
          nameGap: 40
        },
        series: [
          {
            name: 'Data Points',
            type: 'scatter',
            data: scatterData,
            animationDelay(idx) {
              return idx * 5
            },
            symbolSize: 8
          },
          {
            name: 'Outliers',
            type: 'scatter',
            data: outliersData,
            itemStyle: {
              color: '#ff0000'
            },
            symbolSize: 8
          },
          {
            name: 'Equation Curve',
            type: 'line',
            data: equationCurveData,
            lineStyle: {
              type: 'dashed',
              width: 2
            },
            itemStyle: {
              color: '#fca858'
            },
            smooth: true
          }
        ],
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: 'none'
            },
            restore: {},
            saveAsImage: {}
          }
        },
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100
          },
          {
            start: 0,
            end: 100,
            handleSize: '80%'
          }
        ],
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '80px',
          containLabel: true
        }
      }

      this.chartInstance.setOption(option)

      // Handle window resize
      this.resizeHandler = () => {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    initChart() {
      // Call renderChart
      this.renderChart()
    },

    resetZoom() {
      if (this.chartInstance) {
        this.chartInstance.dispatchAction({
          type: 'dataZoom',
          start: 0,
          end: 100
        })
      }
    },

    generateEquationCurveData(chartData) {
      const results = this.jobDetail.results || {}
      if (!results.a || !results.b || !results.c) {
        return []
      }

      const a = parseFloat(results.a)
      const b = parseFloat(results.b)
      const c = parseFloat(results.c)

      // Get X-axis range from data
      let minX = Infinity
      let maxX = -Infinity

      chartData.forEach(point => {
        const x = parseFloat(point.SensorValue || point.sensorValue || 0)
        if (!isNaN(x)) {
          if (x < minX) minX = x
          if (x > maxX) maxX = x
        }
      })

      if (minX === Infinity || maxX === -Infinity || minX === maxX) {
        return []
      }

      const curveData = []
      const step = (maxX - minX) / 100

      for (let x = minX; x <= maxX; x += step) {
        const y = a * x * x + b * x + c
        curveData.push([x, y])
      }

      return curveData
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleString()
    },

    async saveEquation() {
      if (!this.editingEquation) {
        swal({
          icon: 'warning',
          title: 'Warning',
          text: 'Please enter an equation'
        })
        return
      }

      this.savingEquation = true
      try {
        await BackTestApiService.createBackTest({
          jobType: 'save-equation',
          parameters: {
            jobId: this.jobId,
            equation: this.editingEquation
          }
        })

        // Update jobDetail with the new equation
        if (
          this.jobDetail &&
          this.jobDetail.details &&
          this.jobDetail.details.results
        ) {
          this.jobDetail.details.results.equation = this.editingEquation
        }

        swal({
          icon: 'success',
          title: 'Success',
          text: 'Equation saved successfully'
        })
      } catch (error) {
        swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save equation'
        })
      } finally {
        this.savingEquation = false
      }
    },

    downloadChartData() {
      if (!this.jobDetail || !this.jobDetail.chartUrl) {
        swal({
          icon: 'warning',
          title: 'Warning',
          text: 'No chart data available'
        })
        return
      }

      try {
        // Create download link from ChartUrl
        const link = document.createElement('a')
        link.href = this.jobDetail.chartUrl
        link.download = `model-generator-${this.jobId}-chart-data.csv`
        link.click()

        swal({
          icon: 'success',
          title: 'Success',
          text: 'Chart data downloaded successfully'
        })
      } catch (error) {
        swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to download chart data'
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.model-generator-detail {
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #666;

    p {
      margin-top: 15px;
      font-size: 16px;
    }
  }

  .detail-content {
    .metadata-section,
    .chart-section,
    .table-section {
      margin-bottom: 15px;
      padding: 20px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #fff;

      h4,
      h5 {
        margin-bottom: 15px;
        font-weight: 600;
      }

      h4 {
        font-size: 18px;
      }

      h5 {
        font-size: 14px;
        color: #666;
      }
    }

    .metadata-content {
      .metadata-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 5px;
        margin-top: 5px;

        .metadata-item {
          label {
            font-weight: 600;
            margin-right: 8px;
            font-size: 13px;
          }

          span {
            font-size: 13px;

            &.status-completed {
              color: #28a745;
              font-weight: 600;
            }

            &.status-failed {
              color: #dc3545;
              font-weight: 600;
            }

            &.status-processing {
              color: #ffc107;
              font-weight: 600;
            }

            &.status-pending {
              color: #17a2b8;
              font-weight: 600;
            }
          }
        }
      }

      .progress-container {
        margin-top: 20px;

        label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          font-size: 13px;
        }
      }

      .error-message {
        margin-top: 20px;
        padding: 15px;
        background-color: #f8d7da;
        border: 1px solid #f5c6cb;
        border-radius: 4px;
        color: #721c24;
        display: flex;
        align-items: center;
        gap: 10px;

        i {
          font-size: 18px;
        }

        span {
          font-size: 14px;
        }
      }

      .parameters-section {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #eee;

        h5 {
          font-size: 13px;
          margin-bottom: 10px;
        }

        .parameters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 5px;

          .parameter-item {
            label {
              font-weight: 600;
              margin-right: 8px;
              font-size: 13px;
              color: #555;
            }

            span {
              font-size: 13px;
              color: #333;
            }
          }
        }
      }

      .results-section {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #eee;

        h5 {
          font-size: 13px;
          margin-bottom: 10px;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 5px;

          .result-item {
            label {
              font-weight: 600;
              margin-right: 8px;
              font-size: 13px;
              color: #555;
            }

            span {
              font-size: 13px;
              color: #333;
            }
          }
        }

        .equation-section {
          margin-top: 10px;
          border-top: 1px solid #eee;
          display: flex;
          align-items: center;
          gap: 10px;

          .equation-label {
            font-size: 13px;
            font-weight: 600;
            color: #333;
            flex-shrink: 0;
            white-space: nowrap;
          }

          .equation-input {
            flex: 1;
            min-width: 0;

            ::v-deep input {
              font-family: monospace;
              font-size: 12px;
            }
          }

          .el-button {
            flex-shrink: 0;
          }
        }
      }
    }

    .chart-section {
      .chart-controls {
        display: flex;
        gap: 8px;
        background-color: #f8f9fa;
        border-radius: 4px;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        margin-bottom: 15px;

        .chart-actions {
          display: flex;
          gap: 8px;
        }
      }

      .chart-container {
        width: 100%;
        height: 400px;
      }
    }
  }
}
</style>
